import styled from 'styled-components'

export const KeywordsFilterWrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background: ${({ theme }) => theme.colors.black};

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 0;
    pointer-events: none;
  }
`

export const Panel = styled.div`
  position: relative;
  z-index: 1;
  width: 100vw;
  height: 100vh;
  background: transparent;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: 0;
  box-shadow: none;
  overflow: hidden;
`

export const List = styled.div<{ $dragging: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 6rem 4rem 6rem;
  cursor: ${({ $dragging }) => ($dragging ? 'grabbing' : 'grab')};
  background: transparent;
  z-index: 2;

  scrollbar-width: none;
  background-repeat: repeat-x;
  background-position: left center;
  background-size: 10px 2px;
  background-image: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.5) 0px,
    rgba(255, 255, 255, 0.5) 2px,
    transparent 2px,
    transparent 10px
  );

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    gap: 8px;
    padding: 10px 32px 14px;
  }
`

export const Column = styled.div`
  min-width: max-content;
  max-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 0.5rem 0;
  border-radius: 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    min-width: max-content;
    max-width: none;
    padding: 0.25rem 0;
  }
`

export const ColumnHeader = styled.div`
  padding: 1rem 2rem 0rem 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 5rem;
  font-family: ${({ theme }) => theme.font.regular};
  color: #404040;
  .invisible {
    visibility: hidden;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    font-size: 3.5rem;
    padding: 0.5rem 0.5rem 0.5rem 0.75rem;
  }
`

export const ColumnItems = styled.div<{ $itemCount: number }>`
  padding: 1rem 0.5rem;
  display: flex;
  flex-direction: ${({ $itemCount }) => {
    if ($itemCount <= 4) return 'column'
    return 'row'
  }};
  flex-wrap: wrap;
  align-content: center;
  justify-content: center;
  align-items: center;
  gap: 6px;

  max-height: 100%;
  width: ${({ $itemCount }) => {
    let base = 8
    if ($itemCount <= 4) return 'min-content'
    if ($itemCount <= 10) return base * 2 + 'rem'
    if ($itemCount <= 20) return base * 3 + 'rem'
    if ($itemCount <= 30) return base * 4 + 'rem'
    if ($itemCount <= 40) return base * 5 + 'rem'
    if ($itemCount <= 50) return base * 6 + 'rem'
    if ($itemCount <= 60) return base * 7 + 'rem'
    if ($itemCount <= 70) return base * 8 + 'rem'
    if ($itemCount <= 80) return base * 9 + 'rem'
    return base * 10 + 'rem'
  }};
  max-width: none;

  margin: 0 auto;
  overflow: visible;

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    gap: 5px;
    padding: 0.5rem 0.75rem;
    height: 100%;
  }
`

// Higher value = more dramatic size difference between small and large tags
export const TAG_SIZE_SCALE = 0.2

export const keywordButtonFontSize = (level: number): string => {
  return `${1 + level * TAG_SIZE_SCALE}rem`
}

export const keywordButtonPadding = (level: number): string => {
  if (level <= -2) return '2px 6px'
  if (level === -1) return '3px 7px'
  const paddingScale = 1 + level * 0.5
  return `${4 + level * paddingScale}px ${8 + level * paddingScale * 2}px`
}

export const KeywordButton = styled.button<{ $sizeLevel: number }>`
  border: none;
  max-width: min-content;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black87};
  border-radius: ${({ $sizeLevel }) => Math.max(4, 10 + $sizeLevel * 2)}px;
  border: 1px solid ${({ theme }) => theme.colors.borderGrey};
  text-align: center;
  width: auto;
  white-space: nowrap;
  gap: 4px;
  display: inline-flex;

  padding: ${({ $sizeLevel }) => keywordButtonPadding($sizeLevel)};
  margin: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: ${({ $sizeLevel }) => keywordButtonFontSize($sizeLevel)};
  line-height: 1.3;
  transition: all 0.2s ease;

  .localizedName,
  .localisedName {
    font-family: ${({ theme }) => theme.font.bold};
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.red};
    background: ${({ theme }) => theme.colors.red};
    color: rgba(255, 255, 255, 0.9);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }

  /* Responsive base font size for rem units */
  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    font-size: calc(${({ $sizeLevel }) => keywordButtonFontSize($sizeLevel)} * 0.85);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.lg}) {
    font-size: calc(${({ $sizeLevel }) => keywordButtonFontSize($sizeLevel)} * 1.15);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    font-size: calc(${({ $sizeLevel }) => keywordButtonFontSize($sizeLevel)} * 1.3);
  }
`

export const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100vw;
  padding: 0rem 6rem 4rem;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 6;

  @media (max-width: ${({ theme }) => theme.breakpoints.deviceW.sm}) {
    padding: 8px 12px 12px;
    gap: 8px;
  }
`

export const CountInfo = styled.span`
  min-width: 8rem;
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.72);
  white-space: nowrap;
`

export const RangeLetters = styled.span<{ $hidden?: boolean }>`
  display: inline-block;
  font-size: 2.5rem;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.72);
  position: relative;
  top: -2rem;
  transition: opacity 0.4s ease, transform 0.4s ease;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transform: ${({ $hidden }) => ($hidden ? 'scale(0.5)' : 'scale(1)')};
`

export const ProgressWrap = styled.div`
  position: relative;
  flex: 1;
`

export const FloatingLetter = styled.span<{ $size?: 'small' | 'normal' }>`
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
  line-height: 1;
  letter-spacing: 0.04em;
  pointer-events: none;
  white-space: nowrap;

  ${({ $size, theme }) => {
    if ($size === 'small') {
      return `
        font-weight: light;
        font-size: 2.5rem;
        color: ${theme.colors.grey40};
      `
    }
    return `
      font-size: 4rem;
      color: ${theme.colors.red};
    `
  }}
`

export const FloatingLetterWrap = styled.span`
  position: absolute;
  top: -4rem;
  transform: translateX(-50%);
  left: calc((100% - 8px) * var(--progress) + 4px);
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
  pointer-events: none;
`

export const ProgressInput = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  flex: 1;
  cursor: pointer;
  background: transparent;

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 0;
    width: 10px;
    height: 10px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 100%;
  }
  &::after {
    content: '';
    position: absolute;
    top: 10px;
    right: 0;
    width: 10px;
    height: 10px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 100%;
  }

  &::-webkit-slider-runnable-track {
    height: 2px;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0.7) 0px,
      rgba(255, 255, 255, 0.7) 2px,
      transparent 2px,
      transparent 6px
    );
    background-size: 6px 2px;
    background-repeat: repeat-x;
    background-position: left center;
    border: none;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 9px;
    height: 26px;
    border: none;
    border-radius: 1px;
    background-color: ${({ theme }) => theme.colors.red};
    background-image: repeating-linear-gradient(
      to right,
      rgba(255, 255, 255, 0.9) 0,
      rgba(255, 255, 255, 0.9) 1px,
      transparent 1px,
      transparent 2px
    );
    background-size: 5px 16px;
    background-repeat: no-repeat;
    background-position: center;
    margin-top: -12px;
  }

  &::-moz-range-track {
    height: 2px;
    background-image: linear-gradient(
      to right,
      rgba(255, 255, 255, 0.7) 0px,
      rgba(255, 255, 255, 0.7) 2px,
      transparent 2px,
      transparent 6px
    );
    background-size: 6px 2px;
    background-repeat: repeat-x;
    background-position: left center;
    border: none;
  }

  &::-moz-range-thumb {
    width: 9px;
    height: 26px;
    border: none;
    border-radius: 1px;
    background-color: ${({ theme }) => theme.colors.red};
    background-image: repeating-linear-gradient(
      to right,
      rgba(255, 255, 255, 0.9) 0,
      rgba(255, 255, 255, 0.9) 1px,
      transparent 1px,
      transparent 2px
    );
    background-size: 5px 16px;
    background-repeat: no-repeat;
    background-position: center;
  }
`
